// Lightweight IndexedDB wrapper for Family Repository offline mode.
// Two object stores:
//   - cache: keyed by request key (METHOD + URL), stores last successful response
//   - queue: auto-id, holds writes (POST/PUT/DELETE) made while offline
//
// We intentionally avoid third-party libs (idb/dexie) — IDB native is sufficient.

import { currentActor } from "./sessionPrivacy";
const DB_NAME = "vt_offline";
const DB_VERSION = 2;
const STORE_CACHE = "cache";
const STORE_QUEUE = "queue";

let _dbPromise = null;

function openDb() {
    if (_dbPromise) return _dbPromise;
    _dbPromise = new Promise((resolve, reject) => {
        if (typeof indexedDB === "undefined") {
            reject(new Error("IndexedDB unavailable"));
            return;
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            // Old unscoped private data must never migrate to a new account.
            for (const name of [STORE_CACHE, STORE_QUEUE]) if (db.objectStoreNames.contains(name)) db.deleteObjectStore(name);
            if (!db.objectStoreNames.contains(STORE_CACHE)) {
                db.createObjectStore(STORE_CACHE, { keyPath: "key" });
            }
            if (!db.objectStoreNames.contains(STORE_QUEUE)) {
                db.createObjectStore(STORE_QUEUE, { keyPath: "id", autoIncrement: true });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    }).catch((e) => {
        _dbPromise = null;
        throw e;
    });
    return _dbPromise;
}

function runTx(storeName, mode, fn) {
    return openDb().then(
        (db) =>
            new Promise((resolve, reject) => {
                const tx = db.transaction(storeName, mode);
                const store = tx.objectStore(storeName);
                let result;
                Promise.resolve(fn(store))
                    .then((r) => {
                        result = r;
                    })
                    .catch(reject);
                tx.oncomplete = () => resolve(result);
                tx.onerror = () => reject(tx.error);
                tx.onabort = () => reject(tx.error);
            }),
    );
}

function reqToPromise(req) {
    return new Promise((resolve, reject) => {
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

// ---------------- Cache (GET responses) ----------------

export function cacheKey(method, url) {
    return `${currentActor() || "no-session"}:${(method || "GET").toUpperCase()} ${url}`;
}

export async function putCache(method, url, data) {
    const actor = currentActor();
    const key = cacheKey(method, url);
    if (!actor) return;
    try {
        await runTx(STORE_CACHE, "readwrite", (store) =>
            actor === currentActor() ? reqToPromise(store.put({ key, data, ts: Date.now() })) : null,
        );
    } catch (e) {
        /* idb unavailable — fail silently */
        console.debug("[offlineDb.setCache]", e?.message || e);
    }
}

export async function getCache(method, url) {
    const actor = currentActor();
    if (!actor) return null;
    try {
        const row = await runTx(STORE_CACHE, "readonly", (store) =>
            reqToPromise(store.get(cacheKey(method, url))),
        );
        return actor === currentActor() && row && Date.now() - row.ts < 3600000 ? row : null;
    } catch (e) {
        console.debug("[offlineDb.getCache]", e?.message || e);
        return null;
    }
}

export async function clearCache() {
    try {
        await runTx(STORE_CACHE, "readwrite", (store) => reqToPromise(store.clear()));
    } catch (e) {
        console.debug("[offlineDb.clearCache]", e?.message || e);
    }
}

// ---------------- Write Queue ----------------

export async function enqueueWrite(entry) {
    const actor = currentActor();
    if (!actor) throw new Error("Please sign in before saving offline.");
    // entry = { method, url, data, ts, label? }
    try {
        return await runTx(STORE_QUEUE, "readwrite", (store) =>
            actor === currentActor() ? reqToPromise(store.add({ ...entry, actor, ts: entry.ts || Date.now() })) : null,
        );
    } catch (e) {
        console.debug("[offlineDb.enqueueWrite]", e?.message || e);
        return null;
    }
}

export async function listQueue() {
    try {
        return await runTx(STORE_QUEUE, "readonly", (store) => reqToPromise(store.getAll()));
    } catch (e) {
        console.debug("[offlineDb.listQueue]", e?.message || e);
        return [];
    }
}

export async function removeFromQueue(id) {
    try {
        await runTx(STORE_QUEUE, "readwrite", (store) => reqToPromise(store.delete(id)));
    } catch (e) {
        console.debug("[offlineDb.removeFromQueue]", e?.message || e);
    }
}

export async function queueCount() {
    const items = await listQueue();
    return items.filter(item => item.actor === currentActor()).length;
}

export async function clearQueue() {
    try {
        await runTx(STORE_QUEUE, "readwrite", (store) => reqToPromise(store.clear()));
    } catch (e) {
        console.debug("[offlineDb.clearQueue]", e?.message || e);
    }
}

window.addEventListener('vt:session-changed', async event => {
    const owner = event.detail;
    try {
        for (const storeName of [STORE_CACHE, STORE_QUEUE]) {
            await runTx(storeName, 'readwrite', async store => {
                const rows = await reqToPromise(store.getAll());
                for (const row of rows) {
                    const belongs = storeName === STORE_QUEUE ? row.actor === owner : row.key.startsWith(`${owner}:`);
                    if (!owner || !belongs) store.delete(storeName === STORE_QUEUE ? row.id : row.key);
                }
            });
        }
    } catch { /* IndexedDB unavailable: no data can be served from it. */ }
});
